import Button from '@theme/components/Button/src/tsx/Button';
import FsLightbox from 'fslightbox-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../hooks/useData';
import { useFilter } from '../hooks/useFilter';
import { useHeading } from '../hooks/useHeading';
import type { Feature, FeatureMedia, FeatureView, GetLabel, ShowFeature, ShowLightbox } from '../types/feature';
import Controls from './Controls';
import Dialog from './Dialog';
import Features from './Features';
import Heading from './Heading';

type AppProps = {
	customDescription?: string;
	customTitle?: string;
	hideAllFeaturesButton?: boolean;
	restUrl: string;
	view: FeatureView;
};

interface DialogState {
	features: Feature[];
	index: number;
	isOpen: boolean;
}

export default function App(props: AppProps) {
	const getLabels = () => data?.labels || {};
	const getLabel: GetLabel = (key) => getLabels()[key] || '';

	const rootRef = useRef<HTMLDivElement>(null);
	const { data, loading, error } = useData(props.restUrl);
	const { categoryFilterTerm, setCategoryFilterTerm, searchValue, setSearchValue, clearSearchField, view, handleViewChange } = useFilter({
		data,
		defaultView: props.view,
	});
	const [lightboxImages, setLightboxImages] = useState<FeatureMedia[]>([{ url: '', title: '' }]);
	const [lightboxKey, setLightboxKey] = useState(0);
	const [lightboxSlide, setLightboxSlide] = useState(1);
	const [toggler, setToggler] = useState(false);
	// The features are kept after closing so the content stays visible during the exit transition.
	const [dialogState, setDialogState] = useState<DialogState>({ features: [], index: -1, isOpen: false });
	const activeFeature = dialogState.features[dialogState.index] || null;
	const { headingTitle, headingDescription } = useHeading({
		data,
		customTitle: props.customTitle,
		customDescription: props.customDescription,
		searchValue,
		categoryFilterTerm,
		getLabel,
	});

	const getAllFeaturesButton = () => {
		if (props.hideAllFeaturesButton || !data) {
			return null;
		}

		if (data.isFiltered) {
			return (
				<div className="feature__show-all btn-ctr">
					<Button href={data.featurePageUrl} specifier="center">
						{getLabel('show_me_all')}
					</Button>
				</div>
			);
		} else if (categoryFilterTerm !== 'all') {
			return (
				<div className="feature__show-all btn-ctr">
					<Button onClick={showAllFeatures} specifier="center">
						{getLabel('show_me_all')}
					</Button>
				</div>
			);
		}
	};

	const showAllFeatures = () => {
		setCategoryFilterTerm('all');
		rootRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const showLightbox: ShowLightbox = (key, images, slide) => {
		setLightboxImages(images);
		setLightboxKey(key);
		setLightboxSlide(slide);
		setToggler(!toggler);
	};

	useEffect(() => {
		setToggler(!toggler);
	}, [lightboxImages]);

	const showFeature: ShowFeature = (features, index) => {
		setDialogState({ features, index, isOpen: true });
	};

	const navigateFeature = (index: number) => {
		setDialogState((state) => ({ ...state, index }));
	};

	const lightboxPortal = () => {
		const lightboxWrapper = document.getElementById('lightbox-wrapper');
		if (!lightboxWrapper) {
			throw new Error('Feature lightbox requires #lightbox-wrapper.');
		}

		// `useDialog` opens the lightbox in its own modal dialog, so it stacks above the feature dialog in the top layer.
		return createPortal(
			<FsLightbox
				key={lightboxKey}
				toggler={toggler}
				useDialog={true}
				sources={lightboxImages.map((image) => image['url'])}
				slide={lightboxSlide}
				captions={lightboxImages.map((image, index) => (
					<span key={index}>{image['title']}</span>
				))}
			/>,
			lightboxWrapper,
		);
	};

	if (!loading && !error && data) {
		return (
			<div ref={rootRef}>
				<Heading type="h2" title={headingTitle} description={headingDescription} />
				<Controls
					categories={data.categories}
					clearSearchField={clearSearchField}
					setSearchValue={setSearchValue}
					setCategoryFilterTerm={setCategoryFilterTerm}
					handleViewChange={handleViewChange}
					getLabel={getLabel}
					searchValue={searchValue}
					categoryFilterTerm={categoryFilterTerm}
					view={view}
					isFiltered={data.isFiltered}
				/>
				<Features
					categories={data.categories}
					categoryFilterTerm={categoryFilterTerm}
					editions={data.editions}
					features={data.features}
					getLabel={getLabel}
					searchValue={searchValue}
					showLightbox={showLightbox}
					showFeature={showFeature}
					useListView={view === 'list'}
					isFiltered={data.isFiltered}
				/>
				{getAllFeaturesButton()}
				<Dialog
					feature={activeFeature}
					isOpen={dialogState.isOpen}
					index={dialogState.index}
					total={dialogState.features.length}
					onNavigate={navigateFeature}
					onClose={() => setDialogState((state) => ({ ...state, isOpen: false }))}
					categories={data.categories}
					editions={data.editions}
					getLabel={getLabel}
					showLightbox={showLightbox}
				/>
				{lightboxPortal()}
			</div>
		);
	}

	return null;
}
